import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save, Loader2, ChevronDown, ChevronLeft, Monitor, Tablet, Smartphone,
  ExternalLink, RotateCcw, Home, Info, Phone, Shield, PanelTop,
  PanelBottom, MapPin, Plus, X, Eye, EyeOff, Paintbrush
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import RichTextEditor from '../../components/RichTextEditor';
import ImageUpload from '../../components/ImageUpload';
import { AdminNav } from './AdminDashboard';
import { useAuth } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import API from '../../lib/api';
import { PAGE_SCHEMAS } from '../../lib/content';
import { getImageUrl } from '../../lib/utils-img';

const inputCls = 'bg-[#232323] border-white/[0.10] rounded-lg h-10 text-sm text-white placeholder:text-white/25 focus:border-gold focus:ring-1 focus:ring-gold/20';
const labelCls = 'text-[10px] uppercase tracking-widest text-white/35 mb-1.5 block font-medium';

const PAGE_ICONS = {
  homepage: Home, about: Info, contact: Phone,
  policies: Shield, locations: MapPin, header: PanelTop, footer: PanelBottom,
};

const STATIC_PAGES = ['homepage', 'about', 'contact', 'policies', 'locations', 'header', 'footer'];

const PREVIEW_PATHS = {
  homepage: '/', about: '/about', contact: '/contact',
  policies: '/policies', locations: '/locations', header: '/', footer: '/',
};

const DEVICE_SIZES = {
  desktop: { width: '100%', icon: Monitor, label: 'Desktop' },
  tablet: { width: '768px', icon: Tablet, label: 'Tablet' },
  mobile: { width: '375px', icon: Smartphone, label: 'Mobile' },
};

const LOCATION_DETAIL_SECTIONS = [
  { title: 'Basic Info', fields: [
    { key: 'name', label: 'Location Name', type: 'text', default: '' },
    { key: 'slug', label: 'URL Slug', type: 'text', default: '' },
    { key: 'short_description', label: 'Short Description', type: 'text', default: '' },
    { key: 'full_description', label: 'Full Description', type: 'textarea', default: '' },
  ]},
  { title: 'Images', fields: [
    { key: 'hero_image', label: 'Hero Image', type: 'image', default: '' },
    { key: 'thumbnail_image', label: 'Thumbnail Image', type: 'image', default: '' },
  ]},
  { title: 'SEO', fields: [
    { key: 'seo_title', label: 'SEO Title', type: 'text', default: '' },
    { key: 'seo_description', label: 'SEO Description', type: 'textarea', default: '' },
  ]},
  { title: 'Display Settings', fields: [
    { key: 'is_featured', label: 'Featured', type: 'toggle', default: false },
    { key: 'is_visible', label: 'Visible on Site', type: 'toggle', default: true },
    { key: 'display_order', label: 'Display Order', type: 'number', default: 0 },
  ]},
  { title: 'Page Content Overrides', fields: [
    { key: '__about_label', label: '"About" Section Label', type: 'text', default: 'About' },
    { key: '__properties_label', label: 'Properties Section Label', type: 'text', default: 'Available Properties' },
    { key: '__cta_title', label: 'CTA Heading', type: 'text', default: 'Need Help Choosing?' },
    { key: '__cta_description', label: 'CTA Description', type: 'text', default: '' },
    { key: '__cta_btn', label: 'CTA Button Text', type: 'text', default: 'Contact Us' },
  ]},
];

function FieldEditor({ field, value, onChange }) {
  if (field.type === 'text') {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.default || ''} className={inputCls} data-testid={`field-${field.key}`} />
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.default || ''} rows={3}
          className="w-full bg-[#232323] border border-white/[0.10] rounded-lg p-3 text-sm text-white resize-none focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 placeholder:text-white/25"
          data-testid={`field-${field.key}`} />
      </div>
    );
  }
  if (field.type === 'image') {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <ImageUpload value={value || ''} onChange={onChange} label="" />
      </div>
    );
  }
  if (field.type === 'richtext') {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <RichTextEditor value={value || ''} onChange={onChange} />
      </div>
    );
  }
  if (field.type === 'toggle') {
    return (
      <div className="flex items-center justify-between">
        <label className={`${labelCls} mb-0`}>{field.label}</label>
        <button type="button" onClick={() => onChange(!value)}
          className={`w-10 h-5 rounded-full transition-colors relative ${value ? 'bg-gold' : 'bg-white/10'}`}
          data-testid={`field-${field.key}`}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      </div>
    );
  }
  if (field.type === 'number') {
    return (
      <div>
        <label className={labelCls}>{field.label}</label>
        <Input type="number" value={value ?? ''} onChange={e => onChange(parseInt(e.target.value) || 0)} className={inputCls} data-testid={`field-${field.key}`} />
      </div>
    );
  }
  return null;
}

function SectionAccordion({ section, content, onChange, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden bg-[#1a1a1a]">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
        data-testid={`section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}>
        <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">{section.title}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-white/25 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 space-y-3 border-t border-white/[0.04] pt-3">
              {section.fields.map(field => (
                <FieldEditor key={field.key} field={field} value={content[field.key]} onChange={val => onChange(field.key, val)} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NavItemEditor({ items, onChange, label, addLabel }) {
  const updateItem = (idx, field, value) => {
    const updated = items.map((item, i) => i === idx ? { ...item, [field]: value } : item);
    onChange(updated);
  };
  const addItem = () => onChange([...items, { label: 'New Link', path: '/', visible: true, dropdown: false }]);
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const updated = [...items];
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    onChange(updated);
  };

  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden bg-[#1a1a1a] p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">{label}</span>
        <button type="button" onClick={addItem} className="text-[10px] text-gold hover:text-gold/80 uppercase tracking-widest font-medium" data-testid={`add-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          <Plus className="w-3 h-3 inline mr-1" />{addLabel}
        </button>
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 p-2.5 bg-[#232323] rounded-lg border border-white/[0.06]" data-testid={`nav-item-${idx}`}>
            <div className="flex flex-col gap-0.5 shrink-0">
              <button type="button" onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="text-white/15 hover:text-white disabled:opacity-20 text-[10px] leading-none">&#9650;</button>
              <button type="button" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="text-white/15 hover:text-white disabled:opacity-20 text-[10px] leading-none">&#9660;</button>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-1.5">
              <Input value={item.label} onChange={e => updateItem(idx, 'label', e.target.value)} placeholder="Label" className={`${inputCls} h-8 text-xs`} />
              <Input value={item.path} onChange={e => updateItem(idx, 'path', e.target.value)} placeholder="/path" className={`${inputCls} h-8 text-xs`} />
            </div>
            <button type="button" onClick={() => updateItem(idx, 'visible', !item.visible)}
              className={`p-1 rounded transition-colors shrink-0 ${item.visible !== false ? 'text-gold' : 'text-white/15'}`}
              title={item.visible !== false ? 'Visible' : 'Hidden'}>
              {item.visible !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            </button>
            <button type="button" onClick={() => removeItem(idx)} className="p-1 text-white/15 hover:text-red-400 transition-colors shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminEditor() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [selectedPage, setSelectedPage] = useState('homepage');
  const [device, setDevice] = useState('desktop');
  const [content, setContent] = useState({});
  const [originalContent, setOriginalContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [navSettings, setNavSettings] = useState({ header_items: [], footer_links: [] });
  const [originalNav, setOriginalNav] = useState({ header_items: [], footer_links: [] });
  const [showPreview, setShowPreview] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);

  // Location detail editing
  const [allLocations, setAllLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null); // location object when editing a specific location
  const [locationData, setLocationData] = useState({});
  const [originalLocationData, setOriginalLocationData] = useState({});
  const [locationPageContent, setLocationPageContent] = useState({});
  const [originalLocationPageContent, setOriginalLocationPageContent] = useState({});

  const DEFAULT_HEADER = [
    { label: 'Home', path: '/', dropdown: false, visible: true },
    { label: 'Locations', path: '/locations', dropdown: true, visible: true },
    { label: 'About', path: '/about', dropdown: false, visible: true },
    { label: 'Contact', path: '/contact', dropdown: false, visible: true },
    { label: 'Policies', path: '/policies', dropdown: false, visible: true },
  ];
  const DEFAULT_FOOTER = [
    { label: 'Home', path: '/', visible: true },
    { label: 'Locations', path: '/locations', visible: true },
    { label: 'About', path: '/about', visible: true },
    { label: 'Contact', path: '/contact', visible: true },
    { label: 'Policies', path: '/policies', visible: true },
  ];

  useEffect(() => { if (!authLoading && !isAdmin) navigate('/login'); }, [isAdmin, authLoading, navigate]);

  // Fetch all locations for the location picker
  useEffect(() => {
    API.get('/admin/locations').then(r => setAllLocations(r.data.locations || [])).catch(() => {});
  }, []);

  // Load content for selected static page
  useEffect(() => {
    if (selectedLocation) return; // skip if editing a location detail
    setLoading(true);
    const schema = PAGE_SCHEMAS[selectedPage];
    if (!schema) { setLoading(false); return; }
    const defaults = {};
    schema.sections.forEach(s => s.fields.forEach(f => { defaults[f.key] = f.default; }));

    API.get(`/pages/${selectedPage}`).then(r => {
      const merged = { ...defaults, ...(r.data || {}) };
      setContent(merged);
      setOriginalContent(merged);
      setLoading(false);
    }).catch(() => {
      setContent(defaults);
      setOriginalContent(defaults);
      setLoading(false);
    });
  }, [selectedPage, selectedLocation]);

  // Load nav settings once
  useEffect(() => {
    API.get('/nav').then(r => {
      const nav = r.data.navigation || {};
      const headerItems = nav.header_items?.length ? nav.header_items : DEFAULT_HEADER;
      const footerLinks = nav.footer_links?.length ? nav.footer_links : DEFAULT_FOOTER;
      setNavSettings({ header_items: headerItems, footer_links: footerLinks });
      setOriginalNav({ header_items: JSON.parse(JSON.stringify(headerItems)), footer_links: JSON.parse(JSON.stringify(footerLinks)) });
    }).catch(() => {});
  }, []);

  // Load location detail data when a specific location is selected
  const selectLocation = useCallback((loc) => {
    setSelectedLocation(loc);
    setLoading(true);
    // Load location data
    const locFields = {};
    LOCATION_DETAIL_SECTIONS.forEach(s => s.fields.forEach(f => {
      if (f.key.startsWith('__')) {
        locFields[f.key] = f.default;
      } else {
        locFields[f.key] = loc[f.key] ?? f.default;
      }
    }));

    // Load page content overrides for this location
    API.get(`/pages/location_${loc.slug}`).then(r => {
      const pageOverrides = r.data || {};
      // Merge page overrides into locFields (keys starting with __)
      LOCATION_DETAIL_SECTIONS.forEach(s => s.fields.forEach(f => {
        if (f.key.startsWith('__') && pageOverrides[f.key]) {
          locFields[f.key] = pageOverrides[f.key];
        }
      }));
      setLocationData({ ...locFields });
      setOriginalLocationData({ ...locFields });
      setLoading(false);
    }).catch(() => {
      setLocationData({ ...locFields });
      setOriginalLocationData({ ...locFields });
      setLoading(false);
    });
  }, []);

  const updateField = useCallback((key, value) => {
    if (selectedLocation) {
      setLocationData(prev => ({ ...prev, [key]: value }));
    } else {
      setContent(prev => ({ ...prev, [key]: value }));
    }
  }, [selectedLocation]);

  const hasContentChanges = selectedLocation
    ? JSON.stringify(locationData) !== JSON.stringify(originalLocationData)
    : JSON.stringify(content) !== JSON.stringify(originalContent);
  const hasNavChanges = JSON.stringify(navSettings) !== JSON.stringify(originalNav);
  const hasChanges = hasContentChanges || hasNavChanges;

  const publish = async () => {
    setSaving(true);
    try {
      if (selectedLocation && hasContentChanges) {
        // Split location data into API fields and page content overrides
        const apiFields = {};
        const pageOverrides = {};
        Object.entries(locationData).forEach(([key, value]) => {
          if (key.startsWith('__')) {
            pageOverrides[key] = value;
          } else {
            apiFields[key] = value;
          }
        });
        // Save location data
        await API.put(`/admin/locations/${selectedLocation.id}`, apiFields);
        // Save page content overrides
        if (Object.keys(pageOverrides).length > 0) {
          await API.put(`/admin/pages/location_${locationData.slug || selectedLocation.slug}`, pageOverrides);
        }
        setOriginalLocationData({ ...locationData });
        // Update the location in allLocations
        setAllLocations(prev => prev.map(l => l.id === selectedLocation.id ? { ...l, ...apiFields } : l));
        setSelectedLocation(prev => ({ ...prev, ...apiFields }));
      } else if (hasContentChanges) {
        await API.put(`/admin/pages/${selectedPage}`, content);
        setOriginalContent({ ...content });
      }
      if (hasNavChanges) {
        await API.put('/admin/settings/navigation', navSettings);
        setOriginalNav(JSON.parse(JSON.stringify(navSettings)));
      }
      toast.success('Changes published successfully');
      setIframeKey(k => k + 1);
    } catch (e) {
      toast.error('Failed to publish changes');
    }
    setSaving(false);
  };

  const discard = () => {
    if (selectedLocation) {
      setLocationData({ ...originalLocationData });
    } else {
      setContent({ ...originalContent });
    }
    setNavSettings(JSON.parse(JSON.stringify(originalNav)));
    toast.info('Changes discarded');
  };

  const refreshPreview = () => setIframeKey(k => k + 1);

  const handleSelectPage = (key) => {
    setSelectedPage(key);
    setSelectedLocation(null);
  };

  // Determine current view state
  const isLocationDetail = !!selectedLocation;
  const schema = isLocationDetail ? null : PAGE_SCHEMAS[selectedPage];
  const currentSections = isLocationDetail ? LOCATION_DETAIL_SECTIONS : schema?.sections;

  const previewUrl = isLocationDetail
    ? `${window.location.origin}/locations/${selectedLocation.slug}`
    : `${window.location.origin}${PREVIEW_PATHS[selectedPage] || '/'}`;

  const showLocationPicker = selectedPage === 'locations' && !selectedLocation;

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-editor">
      {/* Top bar */}
      <div className="bg-[#161616] border-b border-white/[0.06] py-4 px-4 md:px-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-gold text-[10px] uppercase tracking-[0.3em] font-semibold">Admin</p>
              <h1 className="font-heading text-xl text-white font-bold">
                Website Editor
                {isLocationDetail && <span className="text-white/30 font-normal text-sm ml-2">/ {selectedLocation.name}</span>}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {hasChanges && (
                <button onClick={discard} className="px-4 py-2 text-xs uppercase tracking-widest text-white/40 hover:text-white border border-white/[0.08] rounded-lg transition-colors" data-testid="discard-btn">
                  <RotateCcw className="w-3 h-3 inline mr-1.5" />Discard
                </button>
              )}
              <Button onClick={publish} disabled={saving || !hasChanges}
                className="bg-gold hover:bg-[#9A7B1F] text-white rounded-lg text-xs uppercase tracking-widest gap-1.5 disabled:opacity-40"
                data-testid="publish-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Publish Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
        <AdminNav active="/admin/editor" />

        <div className="flex gap-6" style={{ minHeight: 'calc(100vh - 240px)' }}>
          {/* Left sidebar */}
          <div className={`${showPreview ? 'w-[420px]' : 'w-full max-w-3xl mx-auto'} shrink-0 flex flex-col gap-4 overflow-y-auto`} style={{ maxHeight: 'calc(100vh - 240px)' }} data-testid="editor-sidebar">
            {/* Page tabs */}
            <div className="flex flex-wrap gap-1.5 p-1.5 bg-[#1a1a1a] rounded-xl border border-white/[0.06]" data-testid="page-tabs">
              {STATIC_PAGES.map(key => {
                const Icon = PAGE_ICONS[key] || Home;
                const s = PAGE_SCHEMAS[key];
                const isActive = selectedPage === key && !isLocationDetail;
                return (
                  <button key={key} onClick={() => handleSelectPage(key)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium rounded-lg transition-all whitespace-nowrap
                      ${isActive ? 'bg-gold text-white shadow-sm' : 'text-white/35 hover:text-white hover:bg-white/[0.05]'}`}
                    data-testid={`tab-${key}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {s?.label?.replace(' Page', '') || key}
                  </button>
                );
              })}
            </div>

            {/* Status bar */}
            {hasChanges && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gold/10 border border-gold/20 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                <span className="text-[11px] text-gold font-medium">Unsaved changes</span>
              </div>
            )}

            {/* Location picker - shows when Locations tab selected and no specific location chosen */}
            {showLocationPicker && (
              <div className="space-y-3" data-testid="location-picker">
                {/* Locations listing page fields */}
                {schema?.sections?.map((section, i) => (
                  <SectionAccordion key={`locations-${i}`} section={section} content={content} onChange={updateField} defaultOpen={i === 0} />
                ))}

                {/* Location detail pages */}
                <div className="border border-white/[0.06] rounded-xl bg-[#1a1a1a] p-4">
                  <span className="text-xs font-semibold text-white/70 uppercase tracking-wider block mb-3">Location Detail Pages</span>
                  <div className="space-y-2">
                    {allLocations.length === 0 ? (
                      <p className="text-xs text-white/25 py-4 text-center">No locations yet. Add locations from the Locations tab.</p>
                    ) : allLocations.map(loc => (
                      <button key={loc.id} onClick={() => selectLocation(loc)}
                        className="w-full flex items-center gap-3 p-3 bg-[#232323] rounded-lg border border-white/[0.06] hover:border-gold/30 transition-all text-left group"
                        data-testid={`edit-location-${loc.slug}`}>
                        <div className="w-10 h-10 rounded-lg bg-white/[0.06] overflow-hidden shrink-0">
                          {loc.thumbnail_image && <img src={getImageUrl(loc.thumbnail_image)} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white/70 group-hover:text-gold transition-colors truncate">{loc.name}</p>
                          <p className="text-[10px] text-white/25 truncate">/locations/{loc.slug}</p>
                        </div>
                        <ChevronDown className="w-3.5 h-3.5 text-white/15 -rotate-90 shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Location detail editor */}
            {isLocationDetail && (
              <div className="space-y-3" data-testid="location-detail-editor">
                <button onClick={() => setSelectedLocation(null)}
                  className="flex items-center gap-1.5 text-xs text-gold hover:text-gold/80 transition-colors"
                  data-testid="back-to-locations-list">
                  <ChevronLeft className="w-3.5 h-3.5" /> Back to Locations
                </button>
                {loading ? (
                  <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
                ) : (
                  LOCATION_DETAIL_SECTIONS.map((section, i) => (
                    <SectionAccordion key={`loc-${i}`} section={section} content={locationData} onChange={updateField} defaultOpen={i === 0} />
                  ))
                )}
              </div>
            )}

            {/* Static page fields (non-locations) */}
            {!showLocationPicker && !isLocationDetail && (
              loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
              ) : (
                <div className="space-y-3 pb-6" data-testid="editor-fields">
                  {schema?.sections?.map((section, i) => (
                    <SectionAccordion key={`${selectedPage}-${i}`} section={section} content={content} onChange={updateField} defaultOpen={i === 0} />
                  ))}
                  {selectedPage === 'header' && (
                    <NavItemEditor items={navSettings.header_items} onChange={items => setNavSettings(prev => ({ ...prev, header_items: items }))} label="Header Menu Items" addLabel="Add Item" />
                  )}
                  {selectedPage === 'footer' && (
                    <NavItemEditor items={navSettings.footer_links} onChange={items => setNavSettings(prev => ({ ...prev, footer_links: items }))} label="Footer Quick Links" addLabel="Add Link" />
                  )}
                </div>
              )
            )}
          </div>

          {/* Right side - Live preview */}
          {showPreview && (
            <div className="flex-1 flex flex-col gap-3 min-w-0" data-testid="preview-panel">
              <div className="flex items-center justify-between px-4 py-2.5 bg-[#1a1a1a] rounded-xl border border-white/[0.06]">
                <div className="flex items-center gap-1">
                  {Object.entries(DEVICE_SIZES).map(([key, { icon: Icon, label }]) => (
                    <button key={key} onClick={() => setDevice(key)}
                      className={`p-2 rounded-lg transition-colors ${device === key ? 'bg-white/[0.08] text-gold' : 'text-white/25 hover:text-white/60'}`}
                      title={label} data-testid={`device-${key}`}>
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={refreshPreview} className="p-2 text-white/25 hover:text-white/60 rounded-lg transition-colors" title="Refresh Preview" data-testid="refresh-preview">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest text-white/35 hover:text-gold border border-white/[0.06] rounded-lg transition-colors"
                    data-testid="open-preview">
                    <ExternalLink className="w-3 h-3" /> Open Page
                  </a>
                </div>
              </div>
              <div className="flex-1 bg-[#0a0a0a] rounded-xl border border-white/[0.06] overflow-hidden flex items-start justify-center p-4" data-testid="preview-container">
                <div style={{ width: DEVICE_SIZES[device].width, maxWidth: '100%', transition: 'width 0.3s ease' }} className="h-full">
                  <iframe key={iframeKey} ref={iframeRef} src={previewUrl}
                    className="w-full h-full rounded-lg border border-white/[0.04]"
                    style={{ minHeight: '600px', background: '#101010' }}
                    title="Page Preview" data-testid="preview-iframe" />
                </div>
              </div>
            </div>
          )}
        </div>

        <button onClick={() => setShowPreview(p => !p)}
          className="fixed bottom-6 right-6 z-40 w-10 h-10 rounded-full bg-[#1a1a1a] border border-white/[0.08] flex items-center justify-center text-white/40 hover:text-gold hover:border-gold/30 transition-all shadow-xl"
          title={showPreview ? 'Hide Preview' : 'Show Preview'} data-testid="toggle-preview">
          {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
